import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, RefreshCw } from 'lucide-react';

const PartyMaster = () => {
  const { hasRole } = useAuth();

  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingParty, setEditingParty] = useState(null);

  // GSTIN default OFF — we try to fetch from backend if available.
  // If endpoint is missing, we safely default to false (hidden).
  const [gstinEnabled, setGstinEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    preferred_contact: 'WHATSAPP', // NEW
    gstin: '',
  });

  const canEdit = hasRole(['ADMIN', 'OWNER', 'MUNIM']);

  // Simple debounce for search to avoid spamming API.
  const debouncedSearch = useDebouncedValue(search, 350);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchParties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, showInactive]);

  const normalizedFormData = useMemo(() => {
    const trimmed = {
      name: (formData.name || '').trim(),
      contact_person: (formData.contact_person || '').trim() || '',
      phone: (formData.phone || '').trim() || '',
      email: (formData.email || '').trim().toLowerCase() || '',
      address: (formData.address || '').trim() || '',
      preferred_contact: formData.preferred_contact || 'WHATSAPP',
      gstin: (formData.gstin || '').trim().toUpperCase() || '',
    };

    // Normalize preferred_contact strictly
    if (trimmed.preferred_contact !== 'WHATSAPP' && trimmed.preferred_contact !== 'EMAIL') {
      trimmed.preferred_contact = 'WHATSAPP';
    }

    // If GSTIN is disabled, do not send gstin at all (backend keeps it optional).
    if (!gstinEnabled) {
      const { gstin, ...rest } = trimmed;
      return rest;
    }

    // If enabled but empty, remove it so backend stores null/None-ish
    if (!trimmed.gstin) {
      const { gstin, ...rest } = trimmed;
      return rest;
    }

    return trimmed;
  }, [formData, gstinEnabled]);

  const fetchSettings = async () => {
    try {
      const res = await apiClientGetMaybe('/app-settings');
      if (res?.data && typeof res.data.gstin_enabled === 'boolean') {
        setGstinEnabled(res.data.gstin_enabled);
      } else if (res?.data && typeof res.data.gstinEnabled === 'boolean') {
        setGstinEnabled(res.data.gstinEnabled);
      } else {
        setGstinEnabled(false);
      }
    } catch {
      setGstinEnabled(false);
    } finally {
      setSettingsLoaded(true);
    }
  };

  const fetchParties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (!showInactive) params.is_active = true;

      const response = await api.parties.list(params);
      setParties(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to fetch parties');
      setParties([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingParty(null);
    setFormData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      preferred_contact: 'WHATSAPP',
      gstin: '',
    });
  };

  const openEditDialog = (party) => {
    setEditingParty(party);
    setFormData({
      name: party.name || '',
      contact_person: party.contact_person || '',
      phone: party.phone || '',
      email: party.email || '',
      address: party.address || '',
      preferred_contact: party.preferred_contact || 'WHATSAPP',
      gstin: party.gstin || '',
    });
    setDialogOpen(true);
  };

  const validateForm = () => {
    const name = (formData.name || '').trim();
    const address = (formData.address || '').trim();
    const phone = (formData.phone || '').trim();
    const email = (formData.email || '').trim();
    const pref = formData.preferred_contact || 'WHATSAPP';

    if (!name) return 'Party name is required';
    if (!address) return 'Address is required';

    // Base rule: at least one contact
    if (!phone && !email) return 'At least one contact is required: WhatsApp mobile OR email';

    // Preferred rule
    if (pref === 'WHATSAPP' && !phone) return 'Preferred contact is WhatsApp, so WhatsApp mobile is required';
    if (pref === 'EMAIL' && !email) return 'Preferred contact is Email, so Email is required';

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }

    try {
      if (editingParty) {
        await api.parties.update(editingParty.id, normalizedFormData);
        toast.success('Party updated successfully');
      } else {
        await api.parties.create(normalizedFormData);
        toast.success('Party created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchParties();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeactivate = async (party) => {
    if (!party?.id) return;

    const ok = window.confirm(
      `Deactivate "${party.name}"?\n\nNote: This is NOT a hard delete. It will just mark the party inactive.`
    );
    if (!ok) return;

    try {
      await api.parties.delete(party.id);
      toast.success('Party deactivated');
      fetchParties();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to deactivate party');
    }
  };

  const contactHint = useMemo(() => {
    const pref = formData.preferred_contact || 'WHATSAPP';
    if (pref === 'WHATSAPP') return 'WhatsApp preferred. Mobile is required.';
    return 'Email preferred. Email is required.';
  }, [formData.preferred_contact]);

  return (
    <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="party-master-page">
      <div className="flex items-center justify-between border-b border-[#333333] pb-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">
            Party Master
          </h1>
          <p className="text-slate-400 font-sans text-xs tracking-wider uppercase mt-1">
            Customer & Client Ledger Registry
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-11 w-11 flex items-center justify-center bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none transition-colors"
            onClick={fetchParties}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {canEdit && (
            <Dialog
              open={dialogOpen}
              onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
                  data-testid="create-party-button"
                >
                  <Plus className="h-4 w-4" />
                  New Party
                </button>
              </DialogTrigger>

              <DialogContent className="max-w-2xl bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
                <DialogHeader className="border-b border-[#333333] pb-3">
                  <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">
                    {editingParty ? 'Edit Party Ledger' : 'Register New Party'}
                  </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label htmlFor="name" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        Party Name *
                      </label>
                      <input
                        id="name"
                        data-testid="party-name-input"
                        className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="contact_person" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        Contact Person
                      </label>
                      <input
                        id="contact_person"
                        className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                        value={formData.contact_person}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            contact_person: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label htmlFor="preferred_contact" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        Preferred Contact *
                      </label>
                      <select
                        id="preferred_contact"
                        className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                        value={formData.preferred_contact}
                        onChange={(e) =>
                          setFormData({ ...formData, preferred_contact: e.target.value })
                        }
                      >
                        <option value="WHATSAPP">WhatsApp Mobile</option>
                        <option value="EMAIL">Email</option>
                      </select>
                      <div className="text-[10px] font-mono text-slate-400 mt-1 uppercase tracking-wider">
                        {contactHint}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="phone" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        WhatsApp Mobile {formData.preferred_contact === 'WHATSAPP' ? '*' : ''}
                      </label>
                      <input
                        id="phone"
                        className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        placeholder="Preferred WhatsApp mobile number"
                      />
                      <div className="text-[10px] font-mono text-slate-400 mt-1 uppercase tracking-wider leading-relaxed">
                        Provide either mobile or email. If preferred is WhatsApp, mobile becomes mandatory.
                      </div>
                    </div>

                    <div>
                      <label htmlFor="email" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        Email {formData.preferred_contact === 'EMAIL' ? '*' : ''}
                      </label>
                      <input
                        id="email"
                        type="email"
                        className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                      />
                      <div className="text-[10px] font-mono text-slate-400 mt-1 uppercase tracking-wider leading-relaxed">
                        Provide either email or mobile. If preferred is Email, email becomes mandatory.
                      </div>
                    </div>

                    {gstinEnabled ? (
                      <div>
                        <label htmlFor="gstin" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                          GSTIN
                        </label>
                        <input
                          id="gstin"
                          className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                          value={formData.gstin}
                          onChange={(e) =>
                            setFormData({ ...formData, gstin: e.target.value })
                          }
                          placeholder="Optional"
                        />
                      </div>
                    ) : (
                      <div className="hidden" />
                    )}

                    <div className="col-span-2">
                      <label htmlFor="address" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">
                        Address *
                      </label>
                      <input
                        id="address"
                        className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData({ ...formData, address: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
                    <button
                      type="button"
                      className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                      data-testid="party-submit-button"
                    >
                      {editingParty ? 'Update Ledger' : 'Create Ledger'}
                    </button>
                  </div>

                  {!settingsLoaded ? (
                    <div className="border-l-4 border-[#F59E0B] bg-[#2A2A2A] p-3 text-xs font-mono text-slate-300">
                      SYSTEM CHECK: LOADING SETTINGS SYSTEM...
                    </div>
                  ) : !gstinEnabled ? (
                    <div className="border-l-4 border-[#6B7280] bg-[#2A2A2A] p-3 text-xs font-mono text-slate-400">
                      GSTIN INTERFACE INACTIVE (DISABLED BY CONTROLLER SETTINGS)
                    </div>
                  ) : null}
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="bg-[#1E1E1E] border border-[#333333] rounded-none">
        <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              placeholder="SEARCH PARTIES REGISTER..."
              data-testid="party-search-input"
              className="pl-10 w-full h-10 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <button
            type="button"
            className={`h-11 px-4 rounded-none font-mono text-xs uppercase tracking-wider border transition-colors ${
              showInactive
                ? 'bg-[#FF6B00] border-[#FF6B00] text-white hover:bg-[#CC5600]'
                : 'bg-[#2A2A2A] border-[#333333] text-slate-300 hover:bg-[#1E1E1E]'
            }`}
            onClick={() => setShowInactive((v) => !v)}
            title="Toggle inactive parties"
          >
            {showInactive ? 'Showing All' : 'Active Only'}
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-8 font-mono text-xs text-slate-400">LOADING DATA MATRIX...</div>
          ) : parties.length === 0 ? (
            <div className="text-center py-8 font-mono text-xs text-slate-400">
              NO PARTIES REGISTRY RECORD FOUND
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-[#333333] bg-[#2A2A2A]/20">
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Code</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Name</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Preferred</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Contact</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">WhatsApp</th>
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Email</th>
                    {gstinEnabled && <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">GSTIN</th>}
                    <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400">Status</th>
                    {canEdit && <th className="px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-slate-400 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#333333]/30">
                  {parties.map((party, idx) => (
                    <tr
                      key={party.id}
                      data-testid={`party-row-${party.id}`}
                      className={idx % 2 === 0 ? 'bg-[#1E1E1E]' : 'bg-[#1E1E1E]/50'}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-white">
                        {party.code || '-'}
                      </td>
                      <td className="px-4 py-2 text-xs font-sans text-white font-medium">{party.name}</td>
                      <td className="px-4 py-2 text-xs">
                        <span className="font-mono text-[9px] bg-[#2A2A2A] border border-[#333333] px-2 py-0.5 text-slate-300">
                          {(party.preferred_contact || 'WHATSAPP') === 'EMAIL' ? 'EMAIL' : 'WHATSAPP'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-300">{party.contact_person || '-'}</td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-300">{party.phone || '-'}</td>
                      <td className="px-4 py-2 text-xs font-mono text-slate-300">{party.email || '-'}</td>

                      {gstinEnabled && (
                        <td className="px-4 py-2 font-mono text-xs text-white">
                          {party.gstin || '-'}
                        </td>
                      )}

                      <td className="px-4 py-2">
                        {party.is_active ? (
                          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#10B981]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981] inline-block" /> ACTIVE
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#6B7280]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280] shadow-[0_0_6px_#6B7280] inline-block" /> INACTIVE
                          </div>
                        )}
                      </td>

                      {canEdit && (
                        <td className="px-4 py-2 text-right">
                          <div className="flex justify-end gap-1.5">
                            <button
                              type="button"
                              className="p-1.5 bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none transition-colors"
                              data-testid={`edit-party-${party.id}`}
                              onClick={() => openEditDialog(party)}
                              title="Edit Party"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </button>

                            {party.is_active && (
                              <button
                                type="button"
                                className="p-1.5 bg-[#2A2A2A] border border-[#333333] hover:bg-red-950/40 hover:border-red-900 text-red-400 rounded-none transition-colors"
                                data-testid={`deactivate-party-${party.id}`}
                                onClick={() => handleDeactivate(party)}
                                title="Deactivate Party"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Local helper: debounce any value without adding deps.
 */
function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Safely tries GET `${BACKEND_URL}/api/app-settings`
 * If backend doesn't have it yet, it fails safely and GSTIN stays hidden.
 */
async function apiClientGetMaybe(path) {
  try {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const url = `${BACKEND_URL}/api${path.startsWith('/') ? path : `/${path}`}`;
    const token = localStorage.getItem('authToken');
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    return { data: await res.json() };
  } catch {
    return null;
  }
}

export default PartyMaster;
